import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, LogOut, LayoutDashboard } from "lucide-react";
import { NAV_LINKS, BRAND } from "@/data/content";
import { track } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // Scroll listener is set once on mount; `setScrolled` is a stable React setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSignedIn = !!user && typeof user === "object";

  const onLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-2xl border-b border-[#D4AF37]/20 shadow-[0_8px_30px_rgba(10,10,15,0.05)]"
          : "bg-white/55 backdrop-blur-md"
      }`}
    >
      <div
        aria-hidden
        className="h-[2px] w-full"
        style={{ background: "linear-gradient(90deg, #BF953F, #FCF6BA, #D4AF37, #B38728, #FCF6BA, #BF953F)" }}
      />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="logo-shimmer-group group flex flex-none items-baseline gap-2 sm:gap-3"
          onClick={() => setOpen(false)}
        >
          <span className="font-serif text-lg sm:text-2xl tracking-tight text-logo-shimmer-lead transition-colors">
            {BRAND.wordmark.lead}
          </span>
          <span className="font-serif italic text-lg sm:text-2xl text-logo-shimmer">
            {BRAND.wordmark.tail}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 xl:gap-10 mx-10 flex-none">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-[0.18em] uppercase transition-colors ${
                  isActive
                    ? "text-gold font-semibold"
                    : "text-neutral-500 hover:text-neutral-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          {isSignedIn ? (
            <>
              <Link
                to="/dashboard"
                data-testid="navbar-dashboard"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-gold transition"
              >
                <LayoutDashboard size={14} /> Dashboard
              </Link>
              <button
                onClick={onLogout}
                data-testid="navbar-logout"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-gold transition"
              >
                <LogOut size={14} /> Log out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="navbar-login"
                className="text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-gold transition"
              >
                Log in
              </Link>
              <Link
                to="/pricing"
                data-testid="navbar-signup"
                onClick={() => track("CTA Clicked", { location: "navbar", label: "See plans" })}
                className="btn-aurora !py-2 !px-4 !text-xs"
              >
                See plans <ArrowUpRight size={14} />
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-neutral-900 p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-neutral-100 bg-white"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `font-serif text-3xl ${isActive ? "text-gold" : "text-neutral-900"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {isSignedIn ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-dashboard"
                  className="font-serif italic text-3xl text-gold"
                >
                  Dashboard
                </Link>
                <button onClick={onLogout} data-testid="mobile-logout" className="btn-ghost mt-2 self-start">
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-login"
                  className="font-serif text-3xl text-neutral-900"
                >
                  Log in
                </Link>
                <Link
                  to="/pricing"
                  onClick={() => {
                    setOpen(false);
                    track("CTA Clicked", { location: "mobile_menu", label: "See plans" });
                  }}
                  data-testid="mobile-signup"
                  className="btn-gold mt-2 self-start"
                >
                  See plans <ArrowUpRight size={16} />
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
